/* eslint-disable max-lines */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@components/layout/page-header';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useGetPriorityQueue } from '@utils/api/back-office/priority-queue/use-get-priority-queue';
import { useGetQueueSummary } from '@utils/api/back-office/priority-queue/use-get-queue-summary';
import { useRequeueItem } from '@utils/api/back-office/priority-queue/use-requeue-item';
import { useRetryItem } from '@utils/api/back-office/priority-queue/use-retry-item';
import { useDeleteItem } from '@utils/api/back-office/priority-queue/use-delete-item';
import { useBulkRequeueStuck } from '@utils/api/back-office/priority-queue/use-bulk-requeue-stuck';
import { useBulkCleanupCompleted } from '@utils/api/back-office/priority-queue/use-bulk-cleanup-completed';
import { useBulkCleanupDeadLetter } from '@utils/api/back-office/priority-queue/use-bulk-cleanup-dead-letter';
import { formatDate } from '@utils/date';
import Button from '@components/atoms/ui-kit/button.tsx';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal.tsx';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { QueueStatus } from '@utils/types/back-office/priority-queue';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';
import {
  RefreshCcw,
  RotateCcw,
  Trash2,
  Flag,
  Clock,
  Activity,
  CheckCircle2,
  AlertTriangle,
  Cpu,
  XCircle
} from 'lucide-react';
import { getErrorCategoryLabel } from '@utils/types/errors/error-categories';
import { StatusBadge } from '@components/atoms/ui-kit/badge';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params';
import { PriorityBadge } from '@components/back-office/priority-queue/priority-badge';
import { OrgQueueFilter } from '@components/back-office/priority-queue/org-queue-filter';
import {
  SetPriorityDialog,
  type SetPriorityTarget
} from '@components/back-office/priority-queue/set-priority-dialog';
import { GpuConfigModal } from '@components/back-office/priority-queue/gpu-config-modal';
import { QueueItemPanel } from '@components/back-office/priority-queue/queue-item-panel';
import StatWidget from '@components/back-office/shared/stat-widget';

type Row = {
  id: string;
  zoom_id: string;
  priority: number;
  status: QueueStatus;
  created_at: string;
  processing_started_at: string;
  retry_count: number;
  error_message: string;
  error_category: string | null;
  _full_error_message?: string | null;
  is_stuck?: boolean;
  is_ready_for_retry?: boolean;
  organization_id: number | null;
  organization_name: string | null;
};

const ALL_STATUSES = [
  'queued',
  'processing',
  'completed',
  'failed',
  'dead_letter'
] as const;

type BulkAction =
  | 'requeue-stuck'
  | 'cleanup-completed'
  | 'cleanup-dead-letter'
  | null;

type ItemAction = 'requeue' | 'retry' | 'delete';

export default function PriorityQueue() {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const navigate = useNavigate();
  const { searchParams } = useNewAppSearchParams();

  const activeOrgId = searchParams.organization_id
    ? Number(searchParams.organization_id)
    : undefined;
  const activeOrgName =
    (searchParams.organization_name_label as string) || undefined;

  const {
    data,
    isLoading,
    isFetching,
    refetch: refetchQueue
  } = useGetPriorityQueue();

  const {
    data: summary,
    isLoading: summaryLoading,
    refetch: refetchSummary
  } = useGetQueueSummary(activeOrgId);

  const { mutate: requeueItem, isLoading: requeueLoading } = useRequeueItem();
  const { mutate: retryItem, isLoading: retryLoading } = useRetryItem();
  const { mutate: deleteItem, isLoading: deleteLoading } = useDeleteItem();

  const { mutate: bulkRequeueStuck, isLoading: bulkRequeueLoading } =
    useBulkRequeueStuck();
  const {
    mutate: bulkCleanupCompleted,
    isLoading: bulkCleanupCompletedLoading
  } = useBulkCleanupCompleted();
  const {
    mutate: bulkCleanupDeadLetter,
    isLoading: bulkCleanupDeadLetterLoading
  } = useBulkCleanupDeadLetter();

  const [cleanupCompletedOlderThanHours, setCleanupCompletedOlderThanHours] =
    useState<number>(24);
  const [cleanupDeadLetterOlderThanHours, setCleanupDeadLetterOlderThanHours] =
    useState<number>(168);
  const [bulkActionModal, setBulkActionModal] = useState<{
    isOpen: boolean;
    action: BulkAction;
  }>({ isOpen: false, action: null });
  const [gpuConfigModalOpen, setGpuConfigModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<{
    id: string;
    zoom_id?: string;
    status?: QueueStatus;
    action: ItemAction;
  } | null>(null);
  const [forceDelete, setForceDelete] = useState(false);
  const [priorityTarget, setPriorityTarget] =
    useState<SetPriorityTarget | null>(null);
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [panelItemId, setPanelItemId] = useState<string | null>(null);

  const isMutating =
    requeueLoading ||
    retryLoading ||
    deleteLoading ||
    bulkRequeueLoading ||
    bulkCleanupCompletedLoading ||
    bulkCleanupDeadLetterLoading;

  const rows = useMemo<Row[]>(() => {
    if (!data?.result) return [];
    return data.result.map(item => ({
      id: String(item.id),
      zoom_id: String(item.zoom_id ?? ''),
      priority: Number(item.org_priority ?? 0),
      status: item.status as QueueStatus,
      created_at: formatDate(item.created_at || ''),
      processing_started_at: item.processing_started_at
        ? formatDate(item.processing_started_at)
        : '-',
      retry_count: Number(item.retry_count ?? 0),
      error_message: item.error_message
        ? item.error_message.substring(0, 100) +
          (item.error_message.length > 100 ? '...' : '')
        : '-',
      error_category: item.error_category ?? null,
      _full_error_message: item.error_message,
      organization_id: item.organization_id ?? null,
      organization_name: item.organization_name ?? null
    }));
  }, [data]);

  const columns = useMemo<DataGridColumn<Row>[]>(() => {
    return [
      {
        header: t('id'),
        field: 'id',
        maxWidth: 170,
        minWidth: 130,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'number',
        serverOperator: 'eq',
        enableRowCopyOnClick: true,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('zoom-id'),
        field: 'zoom_id',
        minWidth: 270,
        size: 270,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        enableRowCopyOnClick: true,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('organization'),
        field: 'organization_name',
        minWidth: 160,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        cell: ({ row }) => {
          const r = row.original;
          if (!r.organization_id) return <span className="opacity-30">—</span>;
          return (
            <button
              type="button"
              className="text-left text-xs font-medium underline-offset-2 hover:underline truncate max-w-[140px] block"
              style={{ color: getColour('activeText') }}
              onClick={() =>
                navigate(`/back-office/organizations/${r.organization_id}`)
              }
            >
              {r.organization_name}
            </button>
          );
        }
      },
      {
        header: t('priority'),
        field: 'priority',
        maxWidth: 180,
        minWidth: 150,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue }) => (
          <PriorityBadge priority={Number(getValue() ?? 0)} />
        )
      },
      {
        header: t('status'),
        field: 'status',
        maxWidth: 150,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'select',
        serverOperator: 'eq',
        filterOptions: ALL_STATUSES.map(s => ({ label: t(s), value: s })),
        cell: ({ row, getValue }) => {
          const status = getValue() as QueueStatus;
          const label = String(status ?? '');
          return (
            <div className="flex items-center gap-2">
              <StatusBadge status={status} label={label} />
              {Boolean(row.original.is_stuck) && (
                <span className="text-xs font-semibold text-red-600 dark:text-red-400">
                  {t('stuck')}
                </span>
              )}
              {Boolean(row.original.is_ready_for_retry) && (
                <span className="text-xs font-semibold text-orange-600 dark:text-orange-400">
                  {t('ready-for-retry')}
                </span>
              )}
            </div>
          );
        }
      },
      {
        header: t('created'),
        field: 'created_at',
        maxWidth: 210,
        enableSorting: true,
        enableFiltering: false,
        filterVariant: 'text',
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('processing-started'),
        field: 'processing_started_at',
        maxWidth: 210,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'text',
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('retry-count'),
        field: 'retry_count',
        maxWidth: 120,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'number',
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('error-message'),
        field: 'error_message',
        minWidth: 260,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'text',
        cell: ({ getValue, row }) => {
          const truncatedMessage = String(getValue() ?? '');
          const fullMessage = row.original._full_error_message;
          const errorCategory = row.original.error_category;
          if (truncatedMessage === '-' || !fullMessage) return truncatedMessage;
          return (
            <Tooltip
              label={
                <div className="max-w-sm text-left">
                  {errorCategory && (
                    <span
                      className="block text-xs font-semibold uppercase mb-1"
                      style={{ color: getColour('activeText') }}
                    >
                      {getErrorCategoryLabel(errorCategory)}
                    </span>
                  )}
                  <span className="text-xs">{fullMessage}</span>
                </div>
              }
            >
              <span className="truncate block max-w-[240px] cursor-help">
                {truncatedMessage}
              </span>
            </Tooltip>
          );
        }
      },
      {
        header: t('actions'),
        field: 'id',
        minSize: 140,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'text',
        cell: ({ row }) => {
          const r = row.original;
          const status = r.status;
          const canSetPriority = status === 'queued';
          const canRequeue = status === 'processing';
          const canRetry = status === 'failed' || status === 'dead_letter';
          const canDelete =
            status === 'completed' ||
            status === 'dead_letter' ||
            status === 'failed' ||
            status === 'processing' ||
            status === 'queued';

          return (
            <div className="flex gap-1 flex-wrap">
              <Tooltip
                label={
                  canSetPriority
                    ? t('set-priority')
                    : t('priority-not-queued', { status })
                }
              >
                <button
                  type="button"
                  disabled={!canSetPriority || isMutating}
                  className={`h-6 w-6 inline-flex items-center justify-center rounded-2xl border transition ${
                    !canSetPriority || isMutating
                      ? 'opacity-50 cursor-not-allowed border-hairline text-gray-400'
                      : 'border-indigo-500/30 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-500/10'
                  }`}
                  onClick={() =>
                    setPriorityTarget({
                      mode: 'single',
                      itemId: r.id,
                      currentPriority: r.priority,
                      zoomId: r.zoom_id
                    })
                  }
                  aria-label={t('set-priority')}
                >
                  <Flag size={10} />
                </button>
              </Tooltip>

              <Tooltip label={t('requeue')}>
                <button
                  type="button"
                  disabled={!canRequeue || isMutating}
                  className={`h-6 w-6 inline-flex items-center justify-center rounded-2xl border transition ${
                    !canRequeue || isMutating
                      ? 'opacity-50 cursor-not-allowed border-hairline text-gray-400'
                      : 'border-blue-500/30 text-blue-600 dark:text-blue-400 hover:bg-blue-500/10'
                  }`}
                  onClick={() =>
                    setSelectedItem({
                      id: r.id,
                      zoom_id: r.zoom_id,
                      status,
                      action: 'requeue'
                    })
                  }
                  aria-label={t('requeue')}
                >
                  <RefreshCcw size={10} />
                </button>
              </Tooltip>

              <Tooltip label={t('retry')}>
                <button
                  type="button"
                  disabled={!canRetry || isMutating}
                  className={`h-6 w-6 inline-flex items-center justify-center rounded-2xl border transition ${
                    !canRetry || isMutating
                      ? 'opacity-50 cursor-not-allowed border-hairline text-gray-400'
                      : 'border-amber-500/30 text-amber-600 dark:text-amber-400 hover:bg-amber-500/10'
                  }`}
                  onClick={() =>
                    setSelectedItem({
                      id: r.id,
                      zoom_id: r.zoom_id,
                      status,
                      action: 'retry'
                    })
                  }
                  aria-label={t('retry')}
                >
                  <RotateCcw size={10} />
                </button>
              </Tooltip>

              <Tooltip label={t('delete')}>
                <button
                  type="button"
                  disabled={!canDelete || isMutating}
                  className={`h-6 w-6 inline-flex items-center justify-center rounded-2xl border transition ${
                    !canDelete || isMutating
                      ? 'opacity-50 cursor-not-allowed border-hairline text-gray-400'
                      : 'border-red-500/30 text-red-600 dark:text-red-400 hover:bg-red-500/10'
                  }`}
                  onClick={() => {
                    setForceDelete(false);
                    setSelectedItem({
                      id: r.id,
                      zoom_id: r.zoom_id,
                      status,
                      action: 'delete'
                    });
                  }}
                  aria-label={t('delete')}
                >
                  <Trash2 size={10} />
                </button>
              </Tooltip>
            </div>
          );
        }
      }
    ];
  }, [t, getColour, isMutating, navigate]);

  const statusCountsMap = useMemo(() => {
    const map = new Map<
      string,
      { count: number; oldest_age_minutes: number | null }
    >();
    if (summary?.status_counts) {
      for (const item of summary.status_counts) {
        map.set(item.status, {
          count: item.count,
          oldest_age_minutes: item.oldest_item_age_minutes
        });
      }
    }
    return map;
  }, [summary]);

  const queuedCount = statusCountsMap.get('queued')?.count ?? 0;
  const processingCount = statusCountsMap.get('processing')?.count ?? 0;
  const completedCount = statusCountsMap.get('completed')?.count ?? 0;
  const failedCount = statusCountsMap.get('failed')?.count ?? 0;
  const deadLetterCount = statusCountsMap.get('dead_letter')?.count ?? 0;
  const queuedOldestMinutes = statusCountsMap.get('queued')?.oldest_age_minutes;
  const processingOldestMinutes =
    statusCountsMap.get('processing')?.oldest_age_minutes;
  const completedOldestMinutes =
    statusCountsMap.get('completed')?.oldest_age_minutes;
  const deadLetterOldestMinutes =
    statusCountsMap.get('dead_letter')?.oldest_age_minutes;
  const stuckCount = summary?.stuck_processing_count ?? 0;
  const readyForRetryCount = summary?.ready_for_retry_count ?? 0;
  const gpu = summary?.gpu_config;

  const orgCounts = summary?.org_counts ?? null;

  // status_counts is always global — the backend only scopes org_counts. Never
  // use queuedCount to describe an org's queue; it would report the whole
  // platform backlog as that customer's.
  const orgStatusCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const c of orgCounts ?? []) {
      map.set(c.status, (map.get(c.status) ?? 0) + c.count);
    }
    return map;
  }, [orgCounts]);

  const orgQueuedCount = orgStatusCounts.get('queued') ?? 0;

  const handleBulkActionError = (error: unknown) => {
    const err = error as {
      response?: {
        data?: {
          error_description?: string;
          detail?: string;
          message?: string;
        };
      };
    };
    const msg =
      err?.response?.data?.error_description ||
      err?.response?.data?.detail ||
      err?.response?.data?.message ||
      t('an-error-occurred');
    toast.error(msg);
  };

  const handleBulkAction = () => {
    if (bulkActionModal.action === 'requeue-stuck') {
      bulkRequeueStuck(undefined, {
        onSettled: () => {
          setBulkActionModal({ isOpen: false, action: null });
          refetchQueue();
          refetchSummary();
        },
        onError: handleBulkActionError
      });
    } else if (bulkActionModal.action === 'cleanup-completed') {
      bulkCleanupCompleted(
        { older_than_hours: cleanupCompletedOlderThanHours, max_items: 1000 },
        {
          onSettled: () => {
            setBulkActionModal({ isOpen: false, action: null });
            refetchQueue();
            refetchSummary();
          },
          onError: handleBulkActionError
        }
      );
    } else if (bulkActionModal.action === 'cleanup-dead-letter') {
      bulkCleanupDeadLetter(
        { older_than_hours: cleanupDeadLetterOlderThanHours, max_items: 1000 },
        {
          onSettled: () => {
            setBulkActionModal({ isOpen: false, action: null });
            refetchQueue();
            refetchSummary();
          },
          onError: handleBulkActionError
        }
      );
    }
  };

  const handleItemAction = () => {
    if (!selectedItem) return;
    if (selectedItem.action === 'requeue') {
      requeueItem(selectedItem.id, {
        onSettled: () => {
          refetchQueue();
          refetchSummary();
        }
      });
    } else if (selectedItem.action === 'retry') {
      retryItem(selectedItem.id, {
        onSettled: () => {
          refetchQueue();
          refetchSummary();
        }
      });
    } else if (selectedItem.action === 'delete') {
      deleteItem(
        { itemId: selectedItem.id, force: Boolean(forceDelete) },
        {
          onSettled: () => {
            refetchQueue();
            refetchSummary();
          }
        }
      );
    }
    setSelectedItem(null);
    setForceDelete(false);
  };

  const bulkTitle = () => {
    switch (bulkActionModal.action) {
      case 'requeue-stuck':
        return t('requeue-all-stuck');
      case 'cleanup-completed':
        return t('cleanup-completed');
      case 'cleanup-dead-letter':
        return t('cleanup-dead-letter');
      default:
        return t('confirm');
    }
  };

  const bulkCaption = () => {
    switch (bulkActionModal.action) {
      case 'requeue-stuck':
        return t('requeue-stuck-confirmation');
      case 'cleanup-completed':
        return `${t('cleanup-completed-confirmation')} (${t('older-than')}: ${cleanupCompletedOlderThanHours}h)`;
      case 'cleanup-dead-letter':
        return `${t('cleanup-dead-letter-confirmation')} (${t('older-than')}: ${cleanupDeadLetterOlderThanHours}h)`;
      default:
        return t('confirm-action');
    }
  };

  const itemTitle = () => {
    if (!selectedItem) return t('confirm');
    switch (selectedItem.action) {
      case 'requeue':
        return t('requeue-item');
      case 'retry':
        return t('retry-item');
      case 'delete':
        return t('delete-item');
      default:
        return t('confirm');
    }
  };

  const itemCaption = () => {
    if (!selectedItem) return t('confirm-action');
    const meta = [
      selectedItem.id ? `${t('id')}: ${selectedItem.id}` : '',
      selectedItem.zoom_id ? `${t('zoom-id')}: ${selectedItem.zoom_id}` : '',
      selectedItem.status ? `${t('status')}: ${selectedItem.status}` : ''
    ]
      .filter(Boolean)
      .join(' • ');

    if (selectedItem.action === 'delete') {
      const protectedDelete =
        selectedItem.status !== 'completed' &&
        selectedItem.status !== 'dead_letter';
      return (
        <div className="space-y-3">
          <div className="text-sm opacity-80">{meta}</div>
          <div className="text-sm">{t('confirm-action')}</div>
          {protectedDelete && (
            <div
              className="p-3 rounded-2xl border text-sm"
              style={{ borderColor: getColour('border') }}
            >
              <div className="font-semibold text-red-600 dark:text-red-400">
                {t('caution')}
              </div>
              <div className="opacity-80">
                {t('delete-protected-hint') ||
                  'Delete is protected by default. Use force delete only if you are sure.'}
              </div>
              <label className="flex items-center gap-2 mt-3">
                <input
                  type="checkbox"
                  checked={forceDelete}
                  onChange={e => setForceDelete(e.target.checked)}
                />
                <span className="font-semibold text-red-600 dark:text-red-400">
                  {t('force-delete') || 'Force delete (danger)'}
                </span>
              </label>
            </div>
          )}
        </div>
      );
    }
    return (
      <div className="space-y-2">
        <div className="text-sm opacity-80">{meta}</div>
        <div className="text-sm">{t('confirm-action')}</div>
      </div>
    );
  };

  const selectedQueued = useMemo(
    () =>
      rows.filter(r => selectedRowIds.includes(r.id) && r.status === 'queued'),
    [rows, selectedRowIds]
  );

  return (
    <div className="bg-app flex-1 min-h-0 flex flex-col px-4 py-2">
      <PageHeader
        title={t('priority-queue')}
        caption={t('priority-queue-desc')}
      />

      {/* Queue status + GPU */}
      <section className="mb-2">
        <h2
          className="text-sm font-semibold mb-4 uppercase tracking-wide pl-2 mt-2"
          style={{ color: getColour('inactiveText') }}
        >
          {t('queue-status')}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <StatWidget
            title={t('queued')}
            value={queuedCount}
            subtitle={
              queuedOldestMinutes
                ? `${t('oldest')}: ${Math.floor(queuedOldestMinutes)}m`
                : '—'
            }
            icon={<Clock className="h-5 w-5" />}
            variant={queuedCount > 50 ? 'warning' : 'default'}
            loading={summaryLoading}
          />
          <StatWidget
            title={t('processing')}
            value={processingCount}
            subtitle={
              stuckCount > 0
                ? `${t('stuck')}: ${stuckCount}`
                : processingOldestMinutes
                  ? `${t('oldest')}: ${Math.floor(processingOldestMinutes)}m`
                  : '—'
            }
            icon={<Activity className="h-5 w-5" />}
            variant={stuckCount > 0 ? 'danger' : 'success'}
            loading={summaryLoading}
          />
          <StatWidget
            title={t('completed')}
            value={completedCount}
            subtitle={
              completedOldestMinutes
                ? `${t('oldest')}: ${Math.floor(completedOldestMinutes)}m`
                : '—'
            }
            icon={<CheckCircle2 className="h-5 w-5" />}
            variant="success"
            loading={summaryLoading}
          />
          <StatWidget
            title={t('failed')}
            value={failedCount}
            subtitle={
              readyForRetryCount > 0
                ? `${t('ready-for-retry')}: ${readyForRetryCount}`
                : '—'
            }
            icon={<AlertTriangle className="h-5 w-5" />}
            variant={failedCount > 0 ? 'warning' : 'success'}
            loading={summaryLoading}
          />
          <StatWidget
            title={t('dead-letter')}
            value={deadLetterCount}
            subtitle={
              deadLetterOldestMinutes
                ? `${t('oldest')}: ${Math.floor(deadLetterOldestMinutes)}m`
                : '—'
            }
            icon={<XCircle className="h-5 w-5" />}
            variant={deadLetterCount > 0 ? 'danger' : 'success'}
            loading={summaryLoading}
          />
          <StatWidget
            title={t('gpu-config') || 'GPU'}
            value={gpu?.enabled ? t('enabled') : t('disabled')}
            subtitle={`${gpu?.min_gpus ?? 0}–${gpu?.max_gpus ?? 0} GPUs`}
            icon={<Cpu className="h-5 w-5" />}
            variant={gpu?.enabled ? 'success' : 'warning'}
            onClick={() => setGpuConfigModalOpen(true)}
            loading={summaryLoading}
          />
        </div>
      </section>

      {/* Priority bulk actions */}
      <section className="mb-2">
        <div className="flex flex-wrap items-center gap-3">
          {activeOrgId && selectedRowIds.length === 0 && orgQueuedCount > 0 && (
            <Button
              onClick={() =>
                setPriorityTarget({
                  mode: 'bulk-org',
                  organizationId: activeOrgId,
                  organizationName: activeOrgName ?? `#${activeOrgId}`,
                  queuedCount: orgQueuedCount
                })
              }
              label={t('apply-to-all-queued-for-org', {
                count: orgQueuedCount,
                orgName: activeOrgName ?? `#${activeOrgId}`
              })}
              variant="primary"
              size="small"
            />
          )}
          {selectedQueued.length > 0 && (
            <Button
              onClick={() =>
                setPriorityTarget({
                  mode: 'bulk-ids',
                  itemIds: selectedQueued.map(r => Number(r.id)),
                  orgName: activeOrgName
                })
              }
              label={`${t('bulk-set-priority')} (${selectedQueued.length})`}
              variant="primary"
              size="small"
            />
          )}
        </div>

        {/* Org counts strip (only when org filter active). The stat cards above
            are platform-wide; everything in here is scoped to the filter. */}
        {activeOrgId && orgCounts && orgCounts.length > 0 && (
          <div className="mt-3 rounded-2xl border border-hairline bg-app-soft p-3 space-y-2">
            <div className="flex flex-wrap gap-2 items-center">
              <span
                className="text-xs font-semibold opacity-70 mr-1"
                style={{ color: getColour('normalText') }}
              >
                {activeOrgName || `#${activeOrgId}`}
              </span>
              {ALL_STATUSES.filter(s => (orgStatusCounts.get(s) ?? 0) > 0).map(
                s => (
                  <span
                    key={`org-status-${s}`}
                    className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-2xl border border-hairline"
                    style={{ color: getColour('normalText') }}
                  >
                    <span className="opacity-70">{t(s.replace('_', '-'))}</span>
                    <span className="font-semibold">
                      {orgStatusCounts.get(s)}
                    </span>
                  </span>
                )
              )}
            </div>
            {orgQueuedCount > 0 && (
              <div className="flex flex-wrap gap-2 items-center">
                <span
                  className="text-xs opacity-60 mr-1"
                  style={{ color: getColour('normalText') }}
                >
                  {t('queued')}
                </span>
                {orgCounts
                  .filter(c => c.status === 'queued')
                  .sort((a, b) => a.org_priority - b.org_priority)
                  .map(c => (
                    <span
                      key={`${c.status}-${c.org_priority}`}
                      className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-2xl border border-hairline"
                      style={{ color: getColour('normalText') }}
                    >
                      <PriorityBadge priority={c.org_priority} />
                      <span className="font-semibold">{c.count}</span>
                    </span>
                  ))}
              </div>
            )}
          </div>
        )}
      </section>

      {/* Bulk maintenance */}
      <section className="mb-6">
        <h2
          className="text-sm font-semibold mb-4 uppercase tracking-wide pl-2"
          style={{ color: getColour('inactiveText') }}
        >
          {t('bulk-actions') || 'Bulk Actions'}
        </h2>
        <div className="rounded-2xl border border-hairline bg-app-soft shadow-card p-4">
          <div className="flex flex-wrap items-center gap-3">
            <OrgQueueFilter />
            <Button
              onClick={() =>
                setBulkActionModal({ isOpen: true, action: 'requeue-stuck' })
              }
              label={`${t('requeue-all-stuck') || 'Requeue Stuck'} ${stuckCount > 0 ? `(${stuckCount})` : ''}`}
              disabled={stuckCount === 0 || isMutating}
              variant="primary"
              size="small"
            />

            <div className="flex items-stretch gap-2">
              <Tooltip
                label={
                  t('cleanup-completed-older-than-hint') ||
                  'Select how old completed items should be before cleanup'
                }
              >
                <select
                  className="px-2 py-1.5 rounded-2xl border text-xs font-medium min-w-[140px] h-9"
                  style={{
                    borderColor: getColour('border'),
                    backgroundColor: getColour('thirdLayer'),
                    color: getColour('normalText')
                  }}
                  value={cleanupCompletedOlderThanHours}
                  onChange={e =>
                    setCleanupCompletedOlderThanHours(Number(e.target.value))
                  }
                  disabled={isMutating}
                >
                  {[24, 48, 72, 168, 720, 2160, 4320, 8760, 43800].map(h => (
                    <option key={h} value={h}>
                      {h}h
                    </option>
                  ))}
                </select>
              </Tooltip>
              <Button
                onClick={() =>
                  setBulkActionModal({
                    isOpen: true,
                    action: 'cleanup-completed'
                  })
                }
                label={t('cleanup-completed') || 'Cleanup Completed'}
                disabled={completedCount === 0 || isMutating}
                variant="secondary"
                size="small"
              />
            </div>

            <div className="flex items-stretch gap-2">
              <Tooltip
                label={
                  t('cleanup-dead-letter-older-than-hint') ||
                  'Select how old dead letter items should be before cleanup'
                }
              >
                <select
                  className="px-2 py-1.5 rounded-2xl border text-xs font-medium min-w-[140px] h-9"
                  style={{
                    borderColor: getColour('border'),
                    backgroundColor: getColour('thirdLayer'),
                    color: getColour('normalText')
                  }}
                  value={cleanupDeadLetterOlderThanHours}
                  onChange={e =>
                    setCleanupDeadLetterOlderThanHours(Number(e.target.value))
                  }
                  disabled={isMutating}
                >
                  {[24, 48, 72, 168, 720, 2160, 4320, 8760, 43800].map(h => (
                    <option key={h} value={h}>
                      {h}h
                    </option>
                  ))}
                </select>
              </Tooltip>
              <Button
                onClick={() =>
                  setBulkActionModal({
                    isOpen: true,
                    action: 'cleanup-dead-letter'
                  })
                }
                label={t('cleanup-dead-letter') || 'Cleanup Dead Letter'}
                disabled={deadLetterCount === 0 || isMutating}
                variant="danger"
                size="small"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Table */}
      <div className="w-full flex-1 min-h-0 flex flex-col">
        <DataGridV2<Row>
          data={rows}
          columns={columns}
          enableRowSelection
          onRowSelectionChange={(selected: Row[]) =>
            setSelectedRowIds(selected.map(r => r.id))
          }
          enablePagination
          manualPagination
          enableSorting
          manualSorting
          enableFiltering
          manualFiltering
          totalItems={data?.total_results ?? rows.length}
          pageSize={25}
          pageSizeOptions={[25, 50, 75, 100]}
          showColumnFilters={false}
          filterMode="popover"
          fill
          dense
          grid={false}
          striped
          loading={isLoading || isFetching || !data}
          getRowId={row => String(row.id)}
          onRowClick={row => setPanelItemId(row.id)}
        />

        {/* Session detail panel — fetches by ID so it survives list refresh/pagination */}
        <QueueItemPanel
          itemId={panelItemId}
          onClose={() => setPanelItemId(null)}
          isMutating={isMutating}
          onAction={(id, zoomId, status, action) =>
            setSelectedItem({ id, zoom_id: zoomId, status, action })
          }
          onSetPriority={setPriorityTarget}
        />

        {/* Bulk maintenance confirmation */}
        <ConfirmationModal
          isOpen={bulkActionModal.isOpen}
          onClose={() => setBulkActionModal({ isOpen: false, action: null })}
          onConfirm={handleBulkAction}
          size="small"
          title={bulkTitle()}
          caption={bulkCaption()}
          confirmLabel={t('confirm')}
          cancelLabel={t('cancel')}
          destructive={Boolean(bulkActionModal.action?.includes('cleanup'))}
          autoFocusConfirm
        />

        {/* Per-item action confirmation */}
        <ConfirmationModal
          isOpen={!!selectedItem}
          onClose={() => {
            setSelectedItem(null);
            setForceDelete(false);
          }}
          onConfirm={handleItemAction}
          size="small"
          title={itemTitle()}
          caption={itemCaption()}
          confirmLabel={t('confirm')}
          cancelLabel={t('cancel')}
          destructive={selectedItem?.action === 'delete'}
          autoFocusConfirm
        />

        {/* Set priority dialog */}
        <SetPriorityDialog
          isOpen={!!priorityTarget}
          onClose={() => setPriorityTarget(null)}
          onSuccess={() => {
            refetchQueue();
            refetchSummary();
          }}
          target={priorityTarget}
        />

        {/* GPU config modal */}
        <GpuConfigModal
          isOpen={gpuConfigModalOpen}
          onClose={() => setGpuConfigModalOpen(false)}
          gpu={gpu}
        />
      </div>
    </div>
  );
}
